<template>
    <div>
        <!-- Snackbar as live region -->
        <v-snackbar top v-model="notification.show" :color="notification.color" role="status" aria-live="polite" aria-atomic="true" :aria-describedby="'snackbar-text'">
            <v-icon aria-hidden="true">{{ notification.icon }}</v-icon>
            <span id="snackbar-text" class="font-weight-bold pl-2">
                {{ notification.message }}
            </span>
        </v-snackbar>

        <AddQuestionDialog
            :show="addQuestionDialogOptions.show"
            @added="addQuestions"
            @close="addQuestionDialogOptions.show = false"
            @failed="
                notification = $helpers.createNotification('Something went wrong adding your question! Please try again or contact support.', 'mdi-alert', 'orange darken-2')
                addQuestionDialogOptions.question_fetch_loading = false
            "></AddQuestionDialog>

        <div v-if="setup">
            <h1 style="font-weight: 300">Select Questions</h1>
            <p>For comprehensive content it is important to answer readers' questions and problems. Choose some questions you want to answer with your content.</p>
        </div>

        <v-row no-gutters>
            <v-col cols="12" class="ml-auto">
                <v-row class="ml-auto" no-gutters>
                    <v-btn
                        rounded
                        class="ml-auto mb-2"
                        color="primary"
                        :loading="addQuestionDialogOptions.question_fetch_loading"
                        type="button"
                        aria-label="Enter a question manually"
                        @click="addQuestionDialogOptions.show = true">
                        enter manually
                    </v-btn>

                    <span class="mt-2 mx-3">or</span>

                    <v-btn rounded color="primary" type="button" aria-label="Add questions from clipboard" @click="addFromClipboard"> add from clipboard </v-btn>
                </v-row>
            </v-col>
        </v-row>

        <div class="questions-table-area">
            <v-row class="pa-4" role="search" aria-label="Search questions">
                <span>
                    <v-chip class="white--text" color="darkgreen" small role="status" aria-live="polite" :aria-label="selected.length + ' selected'"> {{ selected.length }} selected </v-chip>
                </span>
                <span>
                    <v-chip small class="ml-2" aria-hidden="true" v-if="search.length">{{ resultsStatus }}</v-chip>

                    <!-- SR-Hinweis + Live-Region -->
                    <p id="questions-search-hint" class="sr-only">Type to filter questions. Results update as you type.</p>
                    <p id="questions-count" class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ resultsStatus }}</p></span
                >
                <v-spacer></v-spacer>
                <v-spacer></v-spacer>

                <!-- sichtbarer Ergebniszähler -->

                <v-text-field
                    clearable
                    clear-icon="mdi-close"
                    dense
                    rounded
                    filled
                    v-model="search"
                    max-width="300px"
                    prepend-inner-icon="mdi-magnify"
                    label="Search questions…"
                    :aria-label="'Search questions'"
                    :aria-controls="'cs_comp_table'"
                    :aria-describedby="'questions-search-hint questions-count'"
                    type="search"
                    autocomplete="off"
                    single-line
                    hide-details
                    @click:clear="search = ''"
                    @keydown.esc.stop.prevent="search = ''">
                    <template #prepend-inner>
                        <v-icon aria-hidden="true">mdi-magnify</v-icon>
                    </template>
                </v-text-field>
            </v-row>

            <v-data-table
                id="cs_comp_table"
                ref="questionsTable"
                show-select
                :search="search"
                v-model="selected"
                fixed-header
                height="550px"
                :loading="!questions.length"
                :headers="headers"
                :items="questions"
                class="elevation-0 comp-table"
                @toggle-select-all="selectPage"
                item-key="id"
                :custom-sort="customSort"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc">
                <template v-slot:header.data-table-select="{ on, props }">
                    <v-simple-checkbox
                        v-bind="props"
                        v-on="on"
                        color="primary"
                        class="select-all-checkbox"
                        :aria-label="props.value ? 'Deselect all questions' : 'Select all questions'"
                        :aria-checked="props.indeterminate ? 'mixed' : (!!props.value).toString()"
                        tabindex="0"
                        @keydown.space.prevent="on.click($event)"
                        @keydown.enter.prevent="on.click($event)" />
                </template>
                <!-- header slot (analog SelectTopics/SelectCompetitors) -->
                <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
                    <span :key="h.value" :style="{ width: (header && header.width) || h.width }" :aria-sort="h.sortable ? ariaSort(header) : null">
                        <v-tooltip v-if="h.tooltip" color="primary" top max-width="250px" :open-on-focus="!!h.sortable">
                            <template #activator="{ on, attrs }">
                                <!-- Nur Button wenn sortable -->
                                <v-btn
                                    v-if="h.sortable"
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    text
                                    class="th-btn th-btn--header mr-1"
                                    type="button"
                                    :aria-describedby="`tt-${h.value}`"
                                    @click.stop="toggleSort(h.value)"
                                    @keydown.enter.stop.prevent="toggleSort(h.value)"
                                    @keydown.space.stop.prevent="toggleSort(h.value)">
                                    <span class="th-text">{{ h.text }}</span>
                                    <v-icon x-small aria-hidden="true" class="ml-1">mdi-help</v-icon>
                                </v-btn>
                                <!-- Nicht sortable: passives Label (kein Fokus) -->
                                <span v-else class="th-label mr-1" :aria-describedby="`tt-${h.value}`" v-on="on">
                                    <span class="th-text">{{ h.text }}</span>
                                    <v-icon x-small aria-hidden="true" class="ml-1">mdi-help</v-icon>
                                </span>
                            </template>
                            <span :id="`tt-${h.value}`" role="tooltip">{{ h.tooltip }}</span>
                        </v-tooltip>

                        <!-- Ohne Tooltip -->
                        <v-btn
                            v-else-if="h.sortable"
                            small
                            text
                            class="th-btn th-btn--header"
                            type="button"
                            @click.stop="toggleSort(h.value)"
                            @keydown.enter.stop.prevent="toggleSort(h.value)"
                            @keydown.space.stop.prevent="toggleSort(h.value)">
                            <span class="th-text">{{ h.text }}</span>
                        </v-btn>
                        <span v-else class="th-label">
                            <span class="th-text">{{ h.text }}</span>
                        </span>

                        <!-- SR-Sortstatus -->
                        <span v-if="h.sortable" class="sr-only" aria-live="polite">
                            <span v-if="ariaSort(header) === 'ascending'"> (sorted ascending)</span>
                            <span v-else-if="ariaSort(header) === 'descending'"> (sorted descending)</span>
                        </span>
                    </span>
                </template>

                <!-- Table Body -->
                <template v-slot:body="{ items }">
                    <tbody v-if="items.length">
                        <tr v-for="(item, itemIndex) in items" :key="itemIndex" :class="item.selected ? 'highlighted' : ''">
                            <td class="selecttd">
                                <v-icon class="select-checkbox" v-if="!item.selected" @click="selectItem(itemIndex, item)" style="margin: 0px; padding: 0px">mdi-checkbox-blank-outline</v-icon>
                                <v-icon v-else class="select-checkbox" @click="selectItem(itemIndex, item)" style="margin: 0px; padding: 0px">mdi-checkbox-marked</v-icon>
                            </td>
                            <td v-for="(header, headerIndex) in headers" :key="headerIndex" class="table_td">
                                <!-- ------- -->
                                <!-- question -->
                                <!-- ------- -->
                                <div v-if="header.value === 'question'" class="td_question">
                                    {{ item[header.value] }}
                                </div>
                                <!-- -------------- -->
                                <!---- question_word ----->
                                <!-- -------------- -->
                                <div v-if="header.value === 'question_word'" v-bind="(question = $helpers.getQuestionWord(item))">
                                    <v-chip class="white--text" :color="question.color">
                                        {{ question.text }}
                                    </v-chip>
                                </div>
                                <!-- ------- -->
                                <!-- source -->
                                <!-- ------- -->
                                <div v-if="header.value === 'source'">
                                    <v-chip class="white--text" :color="item.user_input ? 'teal darken-1' : returnSource(item.tags).color" small>{{
                                        item.user_input ? "User Input" : returnSource(item.tags).text
                                    }}</v-chip>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script>
    import AddQuestionDialog from "../../content-suite/AddQuestionDialog.vue"

    export default {
        props: {
            questions: Array,
            setup: Boolean
        },
        components: {
            AddQuestionDialog
        },
        data() {
            return {
                sortBy: [],
                sortDesc: [],
                notification: { show: false },
                addQuestionDialogOptions: {
                    show: false,
                    question_fetch_loading: false
                },
                search: "",
                colors: {
                    h1: "cyan darken-4",
                    h2: "purple darken-4",
                    h4: "pink darken-4",
                    h4: "teal darken-4",
                    h5: "blue darken-4",
                    h6: "red darken-4",
                    people_also_asked: "teal darken-3",
                    CompetitorsContent: "green darken-3"
                },

                headers: [
                    {
                        text: "Question",
                        value: "question",
                        sortable: false
                    },
                    {
                        text: "Question Word",
                        value: "question_word",
                        sortable: true
                    },
                    {
                        text: "Source",
                        value: "source",
                        sortable: true
                    }
                ]
            }
        },
        methods: {
            customSort(items, sortBy, sortDesc) {
                if (!sortBy || !sortBy.length) return items
                const key = sortBy[0]
                const desc = !!(sortDesc && sortDesc[0])

                const getVal = (it) => {
                    if (key === "question_word") {
                        const qw = this.$helpers?.getQuestionWord ? this.$helpers.getQuestionWord(it) : null
                        return (qw && qw.text) || ""
                    }
                    if (key === "source") {
                        return it.user_input ? "User Input" : this.returnSource(it.tags)?.text || ""
                    }
                    return it[key]
                }

                return items.slice().sort((a, b) => {
                    const va = (getVal(a) ?? "").toString().toLowerCase()
                    const vb = (getVal(b) ?? "").toString().toLowerCase()
                    if (va < vb) return desc ? 1 : -1
                    if (va > vb) return desc ? -1 : 1
                    return 0
                })
            },
            toggleSort(field) {
                if (this.sortBy[0] === field) {
                    this.sortDesc = [!this.sortDesc[0]]
                } else {
                    this.sortBy = [field]
                    this.sortDesc = [false]
                }
            },
            ariaSort(header) {
                const by = this.sortBy && this.sortBy[0]
                if (!header || by !== header.value) return "none"
                return this.sortDesc && this.sortDesc[0] ? "descending" : "ascending"
            },
            addFromClipboard() {
                if (!this.$store.state.clipboard_keywords.length) {
                    this.notification = this.$helpers.createNotification("Your clipboard is empty! Please push keywords to your clipboard first.", "mdi-alert", "orange darken-2")
                    return
                }

                console.log(this.$store.state.clipboard_keywords)
                let formatted_clipboard_keywords = this.$store.state.clipboard_keywords.map((x, i) => {
                    let question_object = {}

                    if (this.questions.findIndex((question) => question.question == x.keyword + "?") == -1) {
                        question_object = {
                            question: x.keyword + "?",
                            selected: true,
                            user_input: true
                        }

                        this.questions.unshift(question_object)
                    }
                    return question_object
                })
                console.log(formatted_clipboard_keywords)
                this.$emit("updateQuestions", this.questions)

                this.notification = this.$helpers.createNotification("Keywords added from clipboard!", "mdi-check-circle-outline", "primary")
            },
            addQuestions(question) {
                this.notification = this.$helpers.createNotification("Your question has been added!", "mdi-check-circle-outline", "primary")

                if (this.questions.findIndex((q) => q.question == question) == -1) {
                    let question_object = {
                        question: question,
                        selected: true,
                        user_input: true
                    }
                    this.questions.unshift(question_object)
                }
                this.$emit("updateQuestions", this.questions)
            },
            returnSource(tags) {
                if (tags && tags[0] == "people_also_ask") {
                    return { text: "Google SERP", color: "grey darken-2" }
                } else {
                    if (!tags) {
                        return { text: "Competitors Content", color: this.colors["CompetitorsContent"] }
                    }
                    let obj = { text: "", color: "" }
                    tags.map((tag) => {
                        if (tag.substring(0, 1) == "h") {
                            obj.text = "Competitors " + tag
                            obj.color = this.colors[tag]
                        } else {
                            obj.text = "Competitors Content"
                            obj.color = this.colors["CompetitorsContent"]
                        }
                    })
                    return obj
                }
            },
            selectItem(index, item) {
                let visibleIndex = this.questions.indexOf(item)

                // if (this.selected.length == 10 && this.questions[visibleIndex].selected == false) {
                //     this.notification = this.$helpers.createNotification("You cannot selet more than 10 questions!", "mdi-alert", "orange darken-2")
                //     return
                // }
                if (!this.questions[visibleIndex].selected) {
                    this.questions[visibleIndex].selected = false
                }
                this.questions[visibleIndex].selected = !this.questions[visibleIndex].selected
                // this.$emit("updatequestions", [item])
                this.$emit("updateQuestions", [item])
            },
            selectPage(event) {
                console.log(event.items)
                console.log(this.questions)
                let select = false
                let selectedPageItems = event.items.filter((x) => x.selected)
                if (event.value && selectedPageItems.length == 0) {
                    select = true
                }
                event.items.map((item) => {
                    let kw = this.questions.find((question) => question.question === item.question)
                    if (kw) {
                        kw.selected = select
                    }
                })

                this.$emit("updateQuestions", event.items)

                // console.log(event)
                // if (event.value == true) {
                //     this.questions.map((question, index) => {
                //         console.log(index)
                //         if (index < 10) {
                //             question.selected = true
                //         }
                //     })
                // } else {
                //     this.questions.map((question) => {
                //         question.selected = false
                //     })
                // }
            }
        },
        computed: {
            resultsCount() {
                const t = this.$refs.questionsTable
                if (t && Array.isArray(t.filteredItems)) return t.filteredItems.length
                const all = this.questions || []
                const q = (this.search || "").trim().toLowerCase()
                if (!q) return all.length
                return all.filter((it) => (it.question || "").toLowerCase().includes(q)).length
            },
            // Text für sichtbaren Chip + SR-Live-Region
            resultsStatus() {
                const n = this.resultsCount
                if (!this.search) return `${n} question${n === 1 ? "" : "s"}`
                return `${n} result${n === 1 ? "" : "s"} for “${this.search}”`
            },
            selected: {
                get() {
                    let selectedquestions = this.questions.filter((question) => question.selected)
                    return selectedquestions
                },
                set() {}
            }
        }
    }
</script>

<style scoped>
    .select-checkbox {
        cursor: pointer;
        border-radius: 4px;
        /* für Outline-Kontur */
        outline: none; /* Basis: keine Default-Outline */
    }
    .select-checkbox:focus-visible {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 3px;
    }
</style>
